/**
 * Ticket chat — conversational follow-up on one analyzed ticket.
 *
 * The backend seeds the model with primary sources it loads itself (ticket
 * text + the recorded tool trail from the stored analysis); this client sends
 * only the question and the paired conversation history (rule 7: the seeded
 * analysis prose never travels).
 */
import { API_BASE, getAuthHeaders } from './apiClient';

/** One recorded Compass lookup behind an answer (code-recorded, not model-claimed). */
export interface ChatEvidence {
  tool: string;
  input: unknown;
  ok: boolean;
  chars: number;
  truncated: boolean;
  duration_ms: number;
  cached?: boolean;
}

export interface TicketChatResponse {
  answer: string;
  evidence: ChatEvidence[];
  elapsed_ms: number;
}

export async function askTicketChat(
  ticketKey: string,
  question: string,
  priorQuestions: string[],
): Promise<TicketChatResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/issue-analysis/${encodeURIComponent(ticketKey)}/chat`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ question, prior_questions: priorQuestions }),
    },
  );
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as
      | { detail?: string }
      | null;
    throw new Error(data?.detail || `Ticket chat failed (${response.status})`);
  }
  return response.json();
}
